import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { applyThemeOnBoot } from '@postopia/suite-shell';
import { routeTree } from './routeTree.gen';
import { queryClient } from './lib/queryClient';
import { installEnvHeader } from './lib/envTarget';
import { isStaleChunkError, recoverFromStaleChunk } from './lib/stale-chunk';
import './index.css';
// Suite-shell SCSS — Proton structural + both themes. The activation
// is attribute-driven on <html>, written by applyThemeOnBoot below.
import '@postopia/suite-shell/styles';

// Bootstrap the theme BEFORE React mounts so the very first paint
// is themed (no flash-of-default-styling). Detects product from
// window.location.host; falls back to 'postopia' for localhost dev.
applyThemeOnBoot();

// Stamp every /api/v2/* call with the chosen backend environment. Must run
// BEFORE React mounts so no query escapes unstamped (an unstamped call falls
// to the server default, which may not be the environment the UI is labelling).
installEnvHeader();

// A chunk the current deploy no longer serves: reload once against the fresh
// shell (see lib/stale-chunk.ts). Vite reports preload failures on window; a
// lazy ROUTE import failure is caught by the router, so RouteError covers it.
window.addEventListener('vite:preloadError', (e) => {
  recoverFromStaleChunk((e as Event).type);
});
window.addEventListener('unhandledrejection', (e) => {
  if (isStaleChunkError(e.reason)) recoverFromStaleChunk(String(e.reason?.message ?? e.reason));
});

function RouteError({ error }: { error: unknown }) {
  const stale = isStaleChunkError(error);
  useEffect(() => {
    if (stale) recoverFromStaleChunk('route-error');
  }, [stale]);
  if (stale) return null;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div role="alert" style={{ padding: 24, fontFamily: 'Inter, sans-serif', color: 'var(--c-fg-default)' }}>
      <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>This page failed to load.</p>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--c-fg-secondary)' }}>{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6, border: '1px solid var(--c-border-strong)', background: 'var(--c-bg-surface)', color: 'var(--c-fg-default)', cursor: 'pointer' }}
      >
        Reload
      </button>
    </div>
  );
}

const router = createRouter({ routeTree, defaultErrorComponent: RouteError });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
